import { useMemo, type ReactNode } from 'react';
import { highlight } from '@/components/highlight';
import { SearchSelect, type SearchOption } from '@/components/SearchSelect';
import { compare, matchCode, sameCode } from '@/components/codeMatch';
import { useStorageBins } from '@/hooks/inventory';

export interface PickableBin {
  id: string;
  code: string;
  nameEn: string;
  nameUr?: string | null;
}

/** Words typed anywhere in the bin's name, English or Urdu. */
function matchName(option: SearchOption, query: string): boolean {
  const haystack = `${option.meta ?? ''} ${option.keywords ?? ''}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

/** A code the way StorePicker reads one ("17" finds BIN17), or the name. */
const matchBin = (option: SearchOption, query: string) => matchCode(option, query) || matchName(option, query);

/** The code typed in full ("bin17", "17"), or the name: names are unique
 *  within a store too. Only taken when exactly one bin fits. */
const sameBin = (option: SearchOption, query: string) =>
  sameCode(option, query) || (option.meta ?? '').toLowerCase() === query.trim().toLowerCase();

/**
 * Picks one of a store's storage bins by typing its code or name.
 *
 * A store can hold hundreds of bins, which no dropdown can be scrolled
 * through, so this is a search box: "17", "bin17" or the bin's name all find
 * BIN17, and a code typed in full is taken on Tab without opening the list.
 * Every active bin of a store comes in one request, so the list is filtered
 * here as the user types.
 *
 * `''` is "no bin" — loose stock when receiving, every bin when filtering —
 * and erasing the text (or the ×) goes back to it. `looseOption` adds an
 * explicit `'none'` choice for a filter that tells "loose only" apart from "all".
 */
export function BinPicker({
  bins,
  value,
  onChange,
  label = 'Bin',
  visibleLabel,
  labelClassName,
  placeholder = 'Loose — type a bin code',
  selectedLabel,
  looseOption,
  disabled,
  hint,
}: {
  bins: ReadonlyArray<PickableBin>;
  value: string;
  onChange: (binId: string) => void;
  label?: string;
  visibleLabel?: string;
  labelClassName?: string;
  placeholder?: string;
  /** Shown for a bin the list no longer has — one deleted since it was chosen. */
  selectedLabel?: string | undefined;
  looseOption?: string;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  const options = useMemo(
    () => [
      ...(looseOption ? [{ value: 'none', label: looseOption }] : []),
      ...[...bins]
        .sort((a, b) => compare(a.code, b.code))
        .map((b): SearchOption => ({ value: b.id, label: b.code, meta: b.nameEn, keywords: b.nameUr ?? '' })),
    ],
    [bins, looseOption],
  );

  const known = options.find((o) => o.value === value);
  const shown = known ? (known.meta ? `${known.label} — ${known.meta}` : known.label) : selectedLabel;
  const total = bins.length;

  return (
    <SearchSelect
      label={label}
      visibleLabel={visibleLabel}
      labelClassName={labelClassName}
      value={value}
      selectedLabel={value ? shown : undefined}
      onChange={(option) => onChange(option?.value ?? '')}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      hint={hint}
      emptyText="No bin with that code or name"
      commitExact
      clearable
      clearButton
      match={matchBin}
      isExact={sameBin}
      listHeader={(n) => (n >= total ? `${total} ${total === 1 ? 'bin' : 'bins'}` : `${n} of ${total} bins`)}
      renderOption={(o, q) =>
        o.meta === undefined ? (
          o.label
        ) : (
          <span className="pickopt">
            <span className="pickopt__main pickopt__main--code" title={o.label}>
              {highlight(o.label, q)}
            </span>
            <span className="pickopt__sub" title={o.meta}>
              {highlight(o.meta, q)}
            </span>
          </span>
        )
      }
    />
  );
}

/**
 * A store's bin picker as a form field. Bins are optional, so this renders
 * nothing at all for a store that has none: someone who never set bins up
 * never sees a bin field.
 */
export function BinSelect({
  storeId,
  hint = 'Optional',
  ...rest
}: {
  storeId: string | undefined;
  value: string;
  onChange: (binId: string) => void;
  label?: string;
  placeholder?: string;
  looseOption?: string;
  hint?: ReactNode;
}) {
  const { data: bins } = useStorageBins(storeId);
  if (!bins || bins.length === 0) return null;
  return <BinPicker bins={bins} hint={hint} {...rest} />;
}
